import { useState, useEffect, useRef, useCallback } from "react";

// Topbar configuration
export const topbarConfig = {
  menuToggleLabel: "Toggle menu",
  userMenuLabel: "User menu",
  avatarIcon: "avatar",
  avatarSize: 24,
  greetingPrefix: "Hello",
};

// Get user greeting based on role (directly using the role)
export const getUserRoleGreeting = (user) => {
  if (!user || !user.role) return null;

  // Capitalize first letter of role
  const roleDisplay = user.role.charAt(0).toUpperCase() + user.role.slice(1);
  return `${topbarConfig.greetingPrefix}, ${roleDisplay}`;
};

// Get user info for display
export const getUserDisplayInfo = (user) => {
  if (!user) return null;

  return {
    greeting: getUserRoleGreeting(user),
    name: user.name || user.firstName || "User",
    role: user.role,
    department: user.department,
  };
};

// User menu dropdown configuration
export const userMenuConfig = {
  items: [
    {
      id: "settings",
      label: "Settings",
      icon: "settingsIcon",
      action: "settings",
    },
    {
      id: "change-client",
      label: "Change Client",
      icon: "userManagement",
      action: "change-client",
    },
  ],
};

// Custom hook for user menu management
export const useUserMenu = () => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const handleUserMenuToggle = useCallback(() => {
    setUserMenuOpen((prev) => !prev);
  }, []);

  const closeUserMenu = useCallback(() => {
    setUserMenuOpen(false);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        closeUserMenu();
      }
    };

    if (userMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Also listen for escape key
      const handleEscapeKey = (event) => {
        if (event.key === "Escape") {
          closeUserMenu();
        }
      };
      document.addEventListener("keydown", handleEscapeKey);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleEscapeKey);
      };
    }
  }, [userMenuOpen, closeUserMenu]);

  return {
    userMenuOpen,
    userMenuRef,
    handleUserMenuToggle,
    closeUserMenu,
  };
};

// Menu toggle logic
export const useMenuToggle = (onMenuToggle) => {
  const handleMenuToggle = useCallback(() => {
    if (typeof onMenuToggle === "function") {
      onMenuToggle();
    }
  }, [onMenuToggle]);

  return {
    handleMenuToggle,
  };
};

// User menu item click handler
export const handleUserMenuItemClick = async (
  action,
  closeMenu,
  navigate,
  toastService,
) => {
  try {
    switch (action) {
      case "settings":
        navigate("/change-password");
        break;
      case "change-client":
        navigate("/change-client");
        break;
      default:
        console.warn("Unknown action:", action);
    }
  } catch (error) {
    console.error("Error handling menu item click:", error);
    if (toastService) {
      toastService.error("An error occurred. Please try again.");
    }
  } finally {
    // Close menu after action
    if (typeof closeMenu === "function") {
      closeMenu();
    }
  }
};

// Comprehensive Topbar hook that combines all logic
export const useTopbar = (
  onMenuToggle,
  sidebarOpen,
  navigate,
  toastService,
  user,
) => {
  // User menu management
  const { userMenuOpen, userMenuRef, handleUserMenuToggle, closeUserMenu } =
    useUserMenu();

  // Menu toggle management
  const { handleMenuToggle } = useMenuToggle(onMenuToggle);

  // Menu item click handler
  const handleMenuItemClick = useCallback(
    (action) => {
      handleUserMenuItemClick(action, closeUserMenu, navigate, toastService);
    },
    [closeUserMenu, navigate, toastService],
  );

  // Get user greeting and info
  const userGreeting = getUserRoleGreeting(user);
  const userDisplayInfo = getUserDisplayInfo(user);

  return {
    // User menu state
    userMenuOpen,
    userMenuRef,
    handleUserMenuToggle,
    closeUserMenu,

    // Menu toggle
    handleMenuToggle,

    // Menu item handler
    handleMenuItemClick,

    // Sidebar state
    sidebarOpen,

    // User info
    user,
    userGreeting,
    userDisplayInfo,

    // Config
    config: topbarConfig,
    menuItems: userMenuConfig.items,
  };
};
